/** Phase 3A — AI Performance Lab dashboard.
 *
 * Single-page composite of Overall metrics + Portfolio scorecards + Strategy
 * leaderboard + Model leaderboard + Regime attribution + Decision Intelligence.
 *
 * All server state via TanStack Query (SHORT stale — data updates when trades
 * close). Filters live in URL search params so state survives refresh.
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { Loader2, TrendingUp, TrendingDown, AlertTriangle, Trophy, Activity, Bot, DollarSign, Info, ArrowUpDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import api from "@/lib/api";
import { keys } from "@/lib/queryKeys";
import { STALE, GC } from "@/lib/queryClient";
import EquitySparkline from "@/components/performance/EquitySparkline";
import BenchmarkSection from "@/components/performance/BenchmarkSection";
import MonteCarloSection from "@/components/performance/MonteCarloSection";
import CalibrationSection from "@/components/performance/CalibrationSection";
import DecisionQualitySection from "@/components/performance/DecisionQualitySection";
import TradeAttributionSection from "@/components/performance/TradeAttributionSection";
import WeeklyReviewSection from "@/components/performance/WeeklyReviewSection";
import WalkForwardSection from "@/components/performance/WalkForwardSection";
import OverfittingSection from "@/components/performance/OverfittingSection";
import EdgeSection from "@/components/performance/EdgeSection";
import CalibrationCoachCard from "@/components/CalibrationCoachCard";

const RANGE_PRESETS = [
  { key: "1M", label: "1M", days: 30 },
  { key: "3M", label: "3M", days: 90 },
  { key: "6M", label: "6M", days: 180 },
  { key: "YTD", label: "YTD", ytd: true },
  { key: "1Y", label: "1Y", days: 365 },
  { key: "ALL", label: "All Time", days: null },
];

const STATUS_TONE = {
  insufficient_data: "text-muted-foreground border-border/50",
  experimental: "text-signal-yellow border-current",
  promising:    "pnl-up border-current",
  validated:    "text-signal-green border-current",
  deteriorating:"pnl-down border-current",
  suspended:    "pnl-down border-current",
  healthy:      "pnl-up border-current",
  steady:       "text-signal-yellow border-current",
  at_risk:      "pnl-down border-current",
  underperforming: "text-muted-foreground border-border/50",
};

function fmtUsd(v) {
  if (v == null || !isFinite(v)) return "—";
  const abs = Math.abs(v);
  const sign = v < 0 ? "-" : "";
  if (abs >= 1_000_000) return `${sign}$${(abs/1_000_000).toFixed(2)}M`;
  if (abs >= 1_000) return `${sign}$${(abs/1_000).toFixed(1)}k`;
  return `${sign}$${abs.toFixed(2)}`;
}
function fmtPct(v, digits = 1) {
  if (v == null || !isFinite(v)) return "—";
  return `${v > 0 ? "+" : ""}${v.toFixed(digits)}%`;
}
function fmtNumber(v, digits = 2) {
  if (v == null || !isFinite(v)) return "—";
  return v.toFixed(digits);
}
function toneOf(v) { return v == null || v === 0 ? "" : v > 0 ? "pnl-up" : "pnl-down"; }

// ---- Metric card ----
function MetricCard({ label, value, sub, tone = "", tooltip, testId }) {
  return (
    <div className="grid-cell !p-3" data-testid={testId}>
      <div className="flex items-center gap-1.5">
        <div className="label-cap">{label}</div>
        {tooltip && (
          <Popover>
            <PopoverTrigger className="text-muted-foreground/70 hover:text-foreground focus:outline-none focus:ring-1 focus:ring-current" aria-label={`About ${label}`}>
              <Info size={10}/>
            </PopoverTrigger>
            <PopoverContent className="w-72 text-xs">{tooltip}</PopoverContent>
          </Popover>
        )}
      </div>
      <div className={`mono text-lg mt-1 ${tone}`}>{value}</div>
      {sub && <div className="mono text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function InsufficientBanner({ n }) {
  return (
    <div className="grid-cell border-signal-yellow/50 bg-signal-yellow/5 flex items-center gap-2 text-xs" role="status" data-testid="perf-insufficient-banner">
      <AlertTriangle size={14} className="text-signal-yellow shrink-0"/>
      <div>
        <span className="mono uppercase text-signal-yellow mr-1">Insufficient data</span>
        Only {n} closed trades in this filter set. Metrics are displayed for transparency but rankings and edge claims are gated until sample size grows.
      </div>
    </div>
  );
}

// ---- Filter bar ----
function FilterBar({ params, onChange }) {
  const [portfolios, setPortfolios] = useState([]);
  useMemo(() => {
    api.get("/portfolios").then((r) => setPortfolios(r.data.portfolios || [])).catch(() => {});
  }, []);
  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v && v !== "ALL") next.set(k, v); else next.delete(k);
    onChange(next);
  };
  const activeRange = params.get("range") || "ALL";

  return (
    <div className="grid-cell !p-3 flex items-center gap-3 flex-wrap" data-testid="perf-filter-bar">
      <div className="flex items-center gap-1" role="tablist" aria-label="Time range">
        {RANGE_PRESETS.map((r) => (
          <button
            key={r.key}
            onClick={() => setParam("range", r.key)}
            role="tab"
            aria-selected={activeRange === r.key}
            className={`chip !text-[10px] uppercase border-current transition-colors focus:outline-none focus:ring-1 focus:ring-current ${activeRange === r.key ? "bg-secondary/50 text-foreground" : "text-muted-foreground hover:text-foreground"}`}
            data-testid={`perf-range-${r.key}`}
          >{r.label}</button>
        ))}
      </div>
      <div className="w-px h-4 bg-border/60"/>
      <label className="text-[10px] mono uppercase text-muted-foreground">Portfolio</label>
      <select
        value={params.get("portfolio_key") || ""}
        onChange={(e) => setParam("portfolio_key", e.target.value)}
        className="bg-card border border-border/60 h-7 px-2 text-xs mono focus:outline-none focus:ring-1 focus:ring-current"
        data-testid="perf-filter-portfolio"
        aria-label="Portfolio filter"
      >
        <option value="">All</option>
        {portfolios.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
      </select>
      <label className="text-[10px] mono uppercase text-muted-foreground">Regime</label>
      <select
        value={params.get("regime") || ""}
        onChange={(e) => setParam("regime", e.target.value)}
        className="bg-card border border-border/60 h-7 px-2 text-xs mono focus:outline-none focus:ring-1 focus:ring-current"
        data-testid="perf-filter-regime"
        aria-label="Regime filter"
      >
        <option value="">All</option>
        {["BULL","NEUTRAL","BEAR","VOLATILE","STABLE","MIXED"].map((r) => <option key={r} value={r}>{r}</option>)}
      </select>
    </div>
  );
}

function OverallSection({ overall, sampleSize }) {
  const tone = toneOf(overall.total_return_pct);
  return (
    <section className="space-y-3" aria-labelledby="perf-overall-title">
      <h2 id="perf-overall-title" className="label-cap">Overall Performance · {sampleSize} closed trades</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px bg-border/60">
        <MetricCard label="Starting Cap"   value={fmtUsd(overall.starting_capital)} testId="perf-metric-start"/>
        <MetricCard label="Current Equity" value={fmtUsd(overall.current_equity)}   tone={tone} testId="perf-metric-equity"/>
        <MetricCard label="Total Return"   value={fmtPct(overall.total_return_pct)} tone={tone} testId="perf-metric-total-return"/>
        <MetricCard label="Realized P&L"   value={fmtUsd(overall.realized_pnl)}     tone={toneOf(overall.realized_pnl)} testId="perf-metric-realized"/>
        <MetricCard label="CAGR"           value={overall.cagr_pct != null ? fmtPct(overall.cagr_pct) : "—"} sub={overall.cagr_pct == null ? "< 30 days" : null} tone={tone} testId="perf-metric-cagr" tooltip="Compound Annual Growth Rate. Hidden when the window is under 30 days."/>
        <MetricCard label="Win Rate"       value={fmtPct(overall.win_rate_pct, 0)} sub={`${overall.wins}W / ${overall.losses}L`} testId="perf-metric-winrate"/>
        <MetricCard label="Profit Factor"  value={fmtNumber(overall.profit_factor)} tone={overall.profit_factor >= 1.5 ? "pnl-up" : overall.profit_factor >= 1 ? "" : "pnl-down"} testId="perf-metric-pf" tooltip="Gross wins divided by gross losses. > 1.5 is generally strong."/>
        <MetricCard label="Expectancy"     value={fmtUsd(overall.expectancy_per_trade)} tone={toneOf(overall.expectancy_per_trade)} testId="perf-metric-expectancy" tooltip="Average P&L per trade."/>
        <MetricCard label="Avg Winner"     value={fmtUsd(overall.avg_winner)} tone="pnl-up" testId="perf-metric-avg-winner"/>
        <MetricCard label="Avg Loser"      value={fmtUsd(overall.avg_loser)}  tone="pnl-down" testId="perf-metric-avg-loser"/>
        <MetricCard label="Avg R/R"        value={fmtNumber(overall.avg_reward_risk)} testId="perf-metric-avg-rr"/>
        <MetricCard label="Sharpe"         value={overall.sharpe != null ? fmtNumber(overall.sharpe) : "—"} sub={overall.sharpe == null ? "need ≥5 daily returns" : null} testId="perf-metric-sharpe" tooltip="Risk-adjusted return. Needs at least 5 distinct closing days."/>
        <MetricCard label="Sortino"        value={overall.sortino != null ? fmtNumber(overall.sortino) : "—"} testId="perf-metric-sortino" tooltip="Sortino uses downside volatility only."/>
        <MetricCard label="Max DD"         value={fmtPct(overall.max_drawdown_pct)} tone="pnl-down" testId="perf-metric-maxdd"/>
        <MetricCard label="Recovery Days"  value={overall.recovery_days || "—"} testId="perf-metric-recovery"/>
        <MetricCard label="Avg Hold"       value={overall.avg_holding_days ? `${overall.avg_holding_days}d` : "—"} testId="perf-metric-hold"/>
        <MetricCard label="AI Cost"        value={fmtUsd(overall.ai_cost_usd)} sub={overall.cost_per_profitable_recommendation != null ? `${fmtUsd(overall.cost_per_profitable_recommendation)} / win` : "—"} testId="perf-metric-ai-cost"/>
        <MetricCard label="Return / AI $"  value={overall.return_per_ai_dollar != null ? `${overall.return_per_ai_dollar}×` : "—"} tone={toneOf(overall.return_per_ai_dollar)} testId="perf-metric-return-per-ai" tooltip="Realised P&L divided by AI cost. Higher is better."/>
      </div>
      {overall.equity_curve?.length > 1 && (
        <div className="grid-cell !p-3">
          <div className="label-cap mb-2">Equity Curve · realised P&amp;L only</div>
          <EquitySparkline curve={overall.equity_curve} height={80}/>
        </div>
      )}
    </section>
  );
}

function PortfolioScorecards({ portfolios }) {
  if (!portfolios?.length) return null;
  return (
    <section className="space-y-2" aria-labelledby="perf-portfolios-title">
      <h2 id="perf-portfolios-title" className="label-cap">Portfolio Scorecards</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border/60">
        {portfolios.map((p) => (
          <div key={p.portfolio_key} className="grid-cell !p-3 space-y-2" data-testid={`perf-portfolio-${p.portfolio_key}`}>
            <div className="flex items-center gap-2">
              <div className="mono text-sm">{p.portfolio_name}</div>
              <div className="ml-auto chip !text-[9px] border-current text-muted-foreground uppercase">{p.allocation_pct}% alloc</div>
              <div className={`chip !text-[9px] uppercase ${STATUS_TONE[p.health] || ""}`} data-testid={`perf-portfolio-health-${p.portfolio_key}`}>
                {p.health.replace(/_/g, " ")}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-px bg-border/40 text-xs">
              <div className="p-2 bg-card/40"><div className="label-cap">Return</div><div className={`mono ${toneOf(p.return_pct)}`}>{fmtPct(p.return_pct)}</div></div>
              <div className="p-2 bg-card/40"><div className="label-cap">Trades</div><div className="mono">{p.trade_count}</div></div>
              <div className="p-2 bg-card/40"><div className="label-cap">Win Rate</div><div className="mono">{fmtPct(p.win_rate_pct, 0)}</div></div>
              <div className="p-2 bg-card/40"><div className="label-cap">PF</div><div className="mono">{fmtNumber(p.profit_factor)}</div></div>
              <div className="p-2 bg-card/40"><div className="label-cap">Expect</div><div className={`mono ${toneOf(p.expectancy_per_trade)}`}>{fmtUsd(p.expectancy_per_trade)}</div></div>
              <div className="p-2 bg-card/40"><div className="label-cap">Max DD</div><div className="mono pnl-down">{fmtPct(p.max_drawdown_pct)}</div></div>
              <div className="p-2 bg-card/40"><div className="label-cap">Sharpe</div><div className="mono">{p.sharpe ?? "—"}</div></div>
              <div className="p-2 bg-card/40"><div className="label-cap">Sortino</div><div className="mono">{p.sortino ?? "—"}</div></div>
              <div className="p-2 bg-card/40"><div className="label-cap">Avg Size</div><div className="mono">{fmtUsd(p.avg_position_size)}</div></div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function LeaderTable({ title, rows, columns, testId, statusKey = "status" }) {
  if (!rows?.length) return null;
  return (
    <section className="space-y-2" aria-labelledby={`${testId}-title`}>
      <h2 id={`${testId}-title`} className="label-cap">{title}</h2>
      <div className="grid-cell !p-0 overflow-auto" data-testid={testId}>
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase tracking-widest text-muted-foreground bg-background/20 border-b border-border/40">
            <tr>{columns.map((c) => <th key={c.key} className={`px-3 py-2 font-normal ${c.align === "right" ? "text-right" : "text-left"}`}>{c.label}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={row[columns[0].key] + i} className="border-b border-border/30" data-testid={`${testId}-row-${i}`}>
                {columns.map((c) => {
                  let v = row[c.key];
                  if (c.render) v = c.render(row);
                  const tone = c.tone ? c.tone(row) : "";
                  return <td key={c.key} className={`px-3 py-2 mono ${c.align === "right" ? "text-right" : "text-left"} ${tone}`}>{v ?? "—"}</td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function DecisionIntel({ di }) {
  if (!di || (di.ai_followed?.trade_count === 0 && di.ai_overridden?.trade_count === 0)) return null;
  const rows = [
    { label: "AI followed",     n: di.ai_followed.trade_count, wr: di.ai_followed.win_rate_pct, pf: di.ai_followed.profit_factor, ex: di.ai_followed.expectancy_per_trade, pnl: di.ai_followed.gross_pnl },
    { label: "AI overridden",   n: di.ai_overridden.trade_count, wr: di.ai_overridden.win_rate_pct, pf: di.ai_overridden.profit_factor, ex: di.ai_overridden.expectancy_per_trade, pnl: di.ai_overridden.gross_pnl },
    { label: "Critic accepted", n: di.critic_accepted.trade_count, wr: di.critic_accepted.win_rate_pct, pf: di.critic_accepted.profit_factor, ex: di.critic_accepted.expectancy_per_trade, pnl: di.critic_accepted.gross_pnl },
    { label: "Critic ignored",  n: di.critic_ignored.trade_count, wr: di.critic_ignored.win_rate_pct, pf: di.critic_ignored.profit_factor, ex: di.critic_ignored.expectancy_per_trade, pnl: di.critic_ignored.gross_pnl },
  ];
  return (
    <section className="space-y-2" aria-labelledby="perf-di-title">
      <h2 id="perf-di-title" className="label-cap">Decision Intelligence</h2>
      <div className="grid-cell !p-0 overflow-auto" data-testid="perf-decision-intel">
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase tracking-widest text-muted-foreground bg-background/20 border-b border-border/40">
            <tr>
              <th className="text-left px-3 py-2 font-normal">Decision Path</th>
              <th className="text-right px-3 py-2 font-normal">Trades</th>
              <th className="text-right px-3 py-2 font-normal">Win Rate</th>
              <th className="text-right px-3 py-2 font-normal">PF</th>
              <th className="text-right px-3 py-2 font-normal">Expectancy</th>
              <th className="text-right px-3 py-2 font-normal">P&L</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.label} className="border-b border-border/30" data-testid={`perf-di-row-${r.label.toLowerCase().replace(/ /g,"-")}`}>
                <td className="px-3 py-2 mono">{r.label}</td>
                <td className="px-3 py-2 mono text-right">{r.n || "—"}</td>
                <td className="px-3 py-2 mono text-right">{r.n ? fmtPct(r.wr, 0) : "—"}</td>
                <td className="px-3 py-2 mono text-right">{r.n ? fmtNumber(r.pf) : "—"}</td>
                <td className={`px-3 py-2 mono text-right ${toneOf(r.ex)}`}>{r.n ? fmtUsd(r.ex) : "—"}</td>
                <td className={`px-3 py-2 mono text-right ${toneOf(r.pnl)}`}>{r.n ? fmtUsd(r.pnl) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] mono uppercase text-muted-foreground">
        Overrides · entry: {di.override_summary.entry} · stop: {di.override_summary.stop} · target: {di.override_summary.target} · portfolio: {di.override_summary.portfolio} · qty: {di.override_summary.quantity}
      </div>
    </section>
  );
}

// ---- Container ----
export default function PerformanceLab() {
  const [sp, setSp] = useSearchParams();

  const filters = useMemo(() => {
    const range = sp.get("range") || "ALL";
    const preset = RANGE_PRESETS.find((r) => r.key === range) || RANGE_PRESETS[RANGE_PRESETS.length - 1];
    let dateFrom = null;
    if (preset.days) {
      dateFrom = new Date(Date.now() - preset.days * 86400_000).toISOString();
    } else if (preset.ytd) {
      dateFrom = new Date(new Date().getFullYear(), 0, 1).toISOString();
    }
    return {
      date_from: dateFrom,
      portfolio_key: sp.get("portfolio_key") || null,
      regime: sp.get("regime") || null,
      strategy: sp.get("strategy") || null,
      user_id: sp.get("user_id") || null,
    };
  }, [sp]);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["workspace-perf", filters],
    queryFn: async () => (await api.get("/performance/overview", {
      params: Object.fromEntries(Object.entries(filters).filter(([_, v]) => v)),
    })).data,
    staleTime: STALE.SHORT,
    gcTime: GC.SHORT,
  });

  if (isLoading || !data) {
    return (
      <div className="p-4 md:p-6 space-y-3" data-testid="performance-loading">
        <div className="text-xs text-muted-foreground"><Loader2 className="inline animate-spin mr-2" size={12}/>Loading performance…</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-4 md:p-6 space-y-3">
        <div className="grid-cell border-signal-red/60 bg-signal-red/5 text-xs" role="alert">
          Failed to load performance data. <button onClick={() => refetch()} className="underline underline-offset-2 hover:text-foreground" data-testid="perf-retry">Retry</button>
        </div>
      </div>
    );
  }

  const { meta, overall, portfolios, strategies, models, regime_attribution, decision_intel } = data;
  const sampleSize = meta.sample_size;

  const TABS = [
    { key: "overview",    label: "Overview" },
    { key: "calibration", label: "Calibration" },
    { key: "dq",          label: "Decision Quality" },
    { key: "attribution", label: "Trade Attribution" },
    { key: "weekly",      label: "Weekly Review" },
    { key: "montecarlo",  label: "Monte Carlo" },
    { key: "walkforward", label: "Walk-Forward" },
    { key: "overfitting", label: "Overfitting" },
    { key: "edge",        label: "Edge & Readiness" },
  ];
  const activeTab = sp.get("tab") || "overview";
  const setTab = (t) => {
    const next = new URLSearchParams(sp);
    if (t === "overview") next.delete("tab"); else next.set("tab", t);
    setSp(next);
  };

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="performance-lab">
      <div className="flex items-center gap-3">
        <div className="mono text-xl flex items-center gap-2"><Activity size={18}/>AI Performance Lab</div>
        <div className="text-[10px] mono uppercase text-muted-foreground tracking-widest">
          {sampleSize} trades · {meta.date_range.from ? new Date(meta.date_range.from).toLocaleDateString() : "—"} → {meta.date_range.to ? new Date(meta.date_range.to).toLocaleDateString() : "—"}
        </div>
      </div>

      <div className="flex items-center gap-1 overflow-x-auto pb-1" role="tablist" aria-label="Performance sections" data-testid="perf-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={activeTab === t.key}
            onClick={() => setTab(t.key)}
            className={`chip !text-[10px] uppercase border-current transition-colors focus:outline-none focus:ring-1 focus:ring-current whitespace-nowrap ${activeTab === t.key ? "bg-secondary/60 text-foreground" : "text-muted-foreground hover:text-foreground"}`}
            data-testid={`perf-tab-${t.key}`}
          >{t.label}</button>
        ))}
      </div>

      <FilterBar params={sp} onChange={setSp}/>

      {activeTab === "overview" && (
        <>
          <CalibrationCoachCard compact={false} testId="perf-overview-calibration-coach"/>
          {meta.insufficient_data && <InsufficientBanner n={sampleSize}/>}
          <BenchmarkSection filterParams={filters}/>
          <OverallSection overall={overall} sampleSize={sampleSize}/>
          <PortfolioScorecards portfolios={portfolios}/>
          <LeaderTable
            title="Strategy Leaderboard"
            rows={strategies}
            testId="perf-strategy-leaderboard"
            columns={[
              { key: "strategy",       label: "Strategy",  render: (r) => r.strategy.toUpperCase() },
              { key: "trade_count",    label: "Trades",    align: "right" },
              { key: "win_rate_pct",   label: "Win Rate",  align: "right", render: (r) => fmtPct(r.win_rate_pct, 0) },
              { key: "profit_factor",  label: "PF",        align: "right", render: (r) => fmtNumber(r.profit_factor), tone: (r) => r.profit_factor >= 1.5 ? "pnl-up" : r.profit_factor >= 1 ? "" : "pnl-down" },
              { key: "expectancy",     label: "Expectancy",align: "right", render: (r) => fmtUsd(r.expectancy), tone: (r) => toneOf(r.expectancy) },
              { key: "sharpe",         label: "Sharpe",    align: "right", render: (r) => r.sharpe ?? "—" },
              { key: "max_drawdown_pct", label: "Max DD",  align: "right", render: (r) => fmtPct(r.max_drawdown_pct), tone: () => "pnl-down" },
              { key: "avg_holding_days", label: "Avg Hold",align: "right", render: (r) => r.avg_holding_days ? `${r.avg_holding_days}d` : "—" },
              { key: "status",         label: "Status",    render: (r) => <span className={`chip !text-[9px] uppercase ${STATUS_TONE[r.status] || ""}`}>{r.status.replace(/_/g, " ")}</span> },
            ]}
          />
          <LeaderTable
            title="AI Model Leaderboard"
            rows={models}
            testId="perf-model-leaderboard"
            columns={[
              { key: "model", label: "Model" },
              { key: "call_count", label: "Calls",   align: "right" },
              { key: "trade_count", label: "Trades", align: "right" },
              { key: "win_rate_pct", label: "Win Rate", align: "right", render: (r) => fmtPct(r.win_rate_pct, 0) },
              { key: "profit_factor", label: "PF", align: "right", render: (r) => fmtNumber(r.profit_factor), tone: (r) => r.profit_factor >= 1.5 ? "pnl-up" : "" },
              { key: "ai_cost_usd", label: "AI Cost", align: "right", render: (r) => fmtUsd(r.ai_cost_usd) },
              { key: "return_per_ai_dollar", label: "Return / $", align: "right", render: (r) => r.return_per_ai_dollar != null ? `${r.return_per_ai_dollar}×` : "—", tone: (r) => toneOf(r.return_per_ai_dollar) },
              { key: "avg_response_ms", label: "Avg Latency", align: "right", render: (r) => r.avg_response_ms ? `${r.avg_response_ms}ms` : "—" },
              { key: "failure_count", label: "Failures", align: "right" },
              { key: "status", label: "Status", render: (r) => <span className={`chip !text-[9px] uppercase ${STATUS_TONE[r.status] || ""}`}>{r.status.replace(/_/g, " ")}</span> },
            ]}
          />
          <LeaderTable
            title="Regime Attribution"
            rows={regime_attribution}
            testId="perf-regime-attribution"
            columns={[
              { key: "regime", label: "Regime" },
              { key: "trade_count", label: "Trades", align: "right" },
              { key: "win_rate_pct", label: "Win Rate", align: "right", render: (r) => fmtPct(r.win_rate_pct, 0) },
              { key: "profit_factor", label: "PF", align: "right", render: (r) => fmtNumber(r.profit_factor) },
              { key: "expectancy", label: "Expectancy", align: "right", render: (r) => fmtUsd(r.expectancy), tone: (r) => toneOf(r.expectancy) },
              { key: "gross_pnl", label: "P&L", align: "right", render: (r) => fmtUsd(r.gross_pnl), tone: (r) => toneOf(r.gross_pnl) },
              { key: "avg_holding_days", label: "Avg Hold", align: "right", render: (r) => r.avg_holding_days ? `${r.avg_holding_days}d` : "—" },
              { key: "insufficient_data", label: "Reliability", render: (r) => <span className={`chip !text-[9px] uppercase ${r.insufficient_data ? "text-muted-foreground border-border/50" : "pnl-up border-current"}`}>{r.insufficient_data ? "insufficient" : "reliable"}</span> },
            ]}
          />
          <DecisionIntel di={decision_intel}/>
        </>
      )}

      {activeTab === "calibration" && <CalibrationSection filterParams={filters}/>}
      {activeTab === "dq"          && <DecisionQualitySection filterParams={filters}/>}
      {activeTab === "attribution" && <TradeAttributionSection filterParams={filters}/>}
      {activeTab === "weekly"      && <WeeklyReviewSection filterParams={filters}/>}
      {activeTab === "montecarlo"  && <MonteCarloSection filterParams={filters}/>}
      {activeTab === "walkforward" && <WalkForwardSection/>}
      {activeTab === "overfitting" && <OverfittingSection/>}
      {activeTab === "edge"        && <EdgeSection/>}
    </div>
  );
}
